import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, CreditCard, LogOut, Check, Sparkles, Sun, Moon, Sliders } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

type Plan = 'pro' | 'pro_yearly' | 'elite' | 'elite_yearly' | 'trial' | 'free';

function planLabel(user: { plan?: string | null; role?: string | null; promoCode?: string | null; whopMembershipId?: string | null } | null | undefined): { label: string; tone: 'accent' | 'warn' | 'subtle' } {
  // Admins and promo-code users (no Whop subscription) see 'Complimentary
  // Access' regardless of which plan tier they were granted internally.
  if (user?.role === 'admin' || (user?.promoCode && !user?.whopMembershipId)) {
    return { label: 'Complimentary Access', tone: 'accent' };
  }
  const plan = user?.plan;
  if (plan === 'elite') return { label: 'Elite Monthly', tone: 'accent' };
  if (plan === 'elite_yearly') return { label: 'Elite Yearly', tone: 'accent' };
  if (plan === 'pro') return { label: 'Pro Monthly', tone: 'accent' };
  if (plan === 'pro_yearly') return { label: 'Pro Yearly', tone: 'accent' };
  if (plan === 'trial') return { label: 'Trial', tone: 'warn' };
  return { label: 'Free', tone: 'subtle' };
}

function trialDaysLeft(trialEndsAt: string | null | undefined): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt).getTime();
  if (!isFinite(end)) return null;
  const ms = end - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

type ThemeMode = 'dark' | 'light';
const THEME_STORAGE_KEY = 'telos-theme';

function readThemeFromDom(): ThemeMode {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'light' ? 'light' : 'dark';
}

export function ProfileModal({ open, onClose }: ProfileModalProps) {
  const { user, signOut } = useAuth();
  const { start: startTour } = useTour();
  const navigate = useNavigate();
  const [toast, setToast] = useState<string | null>(null);
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => readThemeFromDom());

  // Re-sync theme state whenever the modal opens (in case the user toggled elsewhere)
  useEffect(() => {
    if (open) setThemeMode(readThemeFromDom());
  }, [open]);

  function handleSwitchTheme() {
    const next: ThemeMode = themeMode === 'dark' ? 'light' : 'dark';
    setThemeMode(next);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
    try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* ignore */ }
    setToast(next === 'dark' ? 'Dark mode' : 'Light mode');
  }

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!user) return null;

  const initials = (user.name || user.email || '?')
    .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const plan = planLabel(user as unknown as { plan?: string|null; role?: string|null; promoCode?: string|null; whopMembershipId?: string|null });
  const isPro = user.plan === 'pro' || user.plan === 'pro_yearly';
  const isTrial = user.plan === 'trial';
  const daysLeft = isTrial ? trialDaysLeft(user.trialEndsAt) : null;

  function handleReplayTour() {
    // Clear the "completed" flag so the autostart logic doesn't fight us, then start.
    try { window.localStorage.removeItem('telos-tour-completed'); } catch { /* ignore */ }
    onClose();
    // Defer so the modal exit animation can play before the tour spotlight appears.
    setTimeout(() => startTour(), 220);
  }

  function handleReplayOnboarding() {
    onClose();
    navigate('/onboarding');
  }

  function handleManageSubscription() {
    if (isPro) {
      window.open('https://whop.com/orders', '_blank', 'noopener,noreferrer');
      return;
    }
    onClose();
    // DashboardRoute listens for this event and switches panels.
    // We also navigate to /dashboard in case we're not already on that route.
    navigate('/');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('telos:navigate-panel', { detail: 'plans' }));
    }, 0);
  }

  function handleSignOut() {
    onClose();
    signOut();
    navigate('/');
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-bg/70 backdrop-blur-sm"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Profile"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'fixed inset-x-4 top-[10vh] sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[440px]',
              'z-50 max-h-[80vh] flex flex-col',
              'bg-bg-elevated border border-border rounded-2xl shadow-elevation',
            )}
          >
            <header className="px-5 py-4 border-b border-border/70 flex items-center justify-between">
              <h2 className="text-base font-bold">Profile</h2>
              <button
                onClick={onClose}
                aria-label="Close"
                className="p-1 -mr-1 rounded text-fg-muted hover:text-fg hover:bg-bg-card transition-colors"
              ><X className="size-4" /></button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {/* Identity */}
              <div className="flex items-center gap-4">
                <div className="size-20 rounded-full bg-gradient-to-br from-accent to-accent-subtle flex items-center justify-center text-bg font-bold text-2xl shadow-glow shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-fg truncate">{user.name || 'Guest'}</div>
                  <div className="text-xs text-fg-muted truncate">{user.email}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge tone={plan.tone}>
                      {isPro && <Sparkles className="size-3" />}
                      {plan.label}
                    </Badge>
                    {isTrial && daysLeft != null && (
                      <span className="text-2xs text-warn font-semibold">
                        {daysLeft <= 0 ? 'Trial expired' : `Ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Buttons stack */}
              <div className="space-y-2">
                <ActionRow
                  icon={themeMode === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
                  label="Switch theme"
                  hint={themeMode === 'dark' ? 'Currently dark — switch to light' : 'Currently light — switch to dark'}
                  onClick={handleSwitchTheme}
                />
                <ActionRow
                  icon={<Sliders className="size-4" />}
                  label="Re-pick trading style & pairs"
                  hint="Go through the setup questions again"
                  onClick={handleReplayOnboarding}
                />
                <ActionRow
                  icon={<RefreshCw className="size-4" />}
                  label="Replay product tour"
                  hint="See the walkthrough again"
                  onClick={handleReplayTour}
                />
                <ActionRow
                  icon={<CreditCard className="size-4" />}
                  label="Manage subscription"
                  hint={isPro ? 'Open billing portal' : 'Upgrade your plan'}
                  onClick={handleManageSubscription}
                />
              </div>

              {/* Sign out */}
              <div className="pt-1">
                <Button
                  variant="danger"
                  className="w-full"
                  onClick={handleSignOut}
                >
                  <LogOut className="size-4" /> Sign out
                </Button>
              </div>
            </div>

            {/* Inline toast (inside modal — keeps focus context) */}
            <AnimatePresence>
              {toast && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                  className={cn(
                    'absolute bottom-4 left-1/2 -translate-x-1/2',
                    'inline-flex items-center gap-2 px-3 py-2 rounded-lg',
                    'bg-bg-card border border-border-strong shadow-elevation',
                    'text-xs font-semibold text-fg',
                  )}
                  role="status"
                >
                  <Check className="size-3.5 text-accent" />
                  {toast}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function ActionRow({
  icon, label, hint, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left',
        'bg-bg-card border border-border/70',
        'hover:border-border-strong hover:bg-bg-elevated',
        'transition-all duration-150 ease-out-expo',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
      )}
    >
      <span className="flex-shrink-0 inline-flex items-center justify-center size-8 rounded-md bg-bg-subtle text-fg-muted group-hover:text-accent transition-colors">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-fg">{label}</span>
        {hint && <span className="block text-2xs text-fg-subtle">{hint}</span>}
      </span>
    </button>
  );
}
